/**
 * Voice capability API — capabilities, analyze, outcome. Contract:
 * interfaces/VoiceObject.ts and product/docs/voice-api-contract.md (NineBackend repo).
 *
 * Unlike most services here, this one keeps the backend `errorCode` (and the
 * `retryable` / `requestId` additional details) on the thrown error: the Voice
 * step chooses its copy and its primary button (Send again / Try again / Close)
 * from them. Same reasoning as AiApiService.imageRevamp.
 */
import { getRequest, postRequest } from "../http/httpRequest";
import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import AppConfig from "../../config/config";
import {
  VOICE_LOCAL_CODES,
  VoiceAnalyzeContext,
  VoiceCapabilities,
  VoiceClip,
  VoiceOutcomeBody,
  VoiceResult,
  VoiceSkillId,
} from "../../interfaces/VoiceObject";

import {
  VoiceApiError,
  isAbortError,
  readVoiceResponse,
  voiceAudioFileName,
} from "../helpers/VoiceApiResponseHelper";
export { VoiceApiError, isAbortError } from "../helpers/VoiceApiResponseHelper";

/** Dev-only console logging. Never logs audio or transcripts. */
export function voiceLog(event: string, data?: Record<string, unknown>): void {
  if (AppConfig.environment === "production") return;
  // eslint-disable-next-line no-console
  console.info(`[voice] ${event}`, data ?? {});
}

// ─── Capabilities (cached per app session) ─────────────────────────────────

/** How long a failed/unavailable (null) capabilities answer is reused. */
const CAPABILITIES_FAILURE_TTL_MS = 60_000;

let capabilitiesPromise: Promise<VoiceCapabilities | null> | null = null;
/** Set when the cached answer is null; after this time the next call refetches. */
let capabilitiesFailureExpiresAt: number | null = null;
const capabilityListeners = new Set<() => void>();

async function fetchCapabilities(): Promise<VoiceCapabilities | null> {
  try {
    const response = await getRequest(
      ServiceEndpoints.VOICE_CAPABILITIES,
      true,
    );
    if (!response.ok) return null;
    const data = await response.json().catch(() => null);
    const caps = data?.contents?.[0] as VoiceCapabilities | undefined;
    return caps && typeof caps === "object" ? caps : null;
  } catch {
    return null;
  }
}

/**
 * Resolves to the server's capabilities, or null when voice is unavailable
 * (fetch failed, non-2xx, unreadable body). Null means "hide voice". A null
 * answer is cached only briefly (CAPABILITIES_FAILURE_TTL_MS) so repeated
 * mounts don't re-request, yet a later visit still retries.
 */
function getCapabilities(): Promise<VoiceCapabilities | null> {
  if (
    capabilitiesPromise &&
    capabilitiesFailureExpiresAt !== null &&
    Date.now() >= capabilitiesFailureExpiresAt
  ) {
    capabilitiesPromise = null;
    capabilitiesFailureExpiresAt = null;
  }
  if (!capabilitiesPromise) {
    const pending = fetchCapabilities();
    capabilitiesPromise = pending;
    pending.then((caps) => {
      if (caps === null && capabilitiesPromise === pending) {
        capabilitiesFailureExpiresAt = Date.now() + CAPABILITIES_FAILURE_TTL_MS;
      }
    });
  }
  return capabilitiesPromise;
}

/** Forget the cached capabilities (after FEATURE_DISABLED / SKILL_DISABLED). */
function invalidateCapabilities(): void {
  capabilitiesPromise = null;
  capabilitiesFailureExpiresAt = null;
  capabilityListeners.forEach((listener) => listener());
}

/** Lets useVoiceCapabilities re-read after an invalidation. */
function subscribeCapabilities(listener: () => void): () => void {
  capabilityListeners.add(listener);
  return () => {
    capabilityListeners.delete(listener);
  };
}

// ─── Analyze ───────────────────────────────────────────────────────────────

/**
 * Sends one recording for analysis. Resolves with the VoiceResult (understood
 * or not). Rejects with VoiceApiError for every failure, or rethrows the
 * AbortError when `signal` aborted the request.
 */
async function analyze(
  skillId: VoiceSkillId,
  clip: VoiceClip,
  context: VoiceAnalyzeContext,
  signal?: AbortSignal,
): Promise<VoiceResult> {
  const endpoint = ServiceEndpoints.VOICE_ANALYZE.replace(
    "{skillId}",
    encodeURIComponent(skillId),
  );
  const form = new FormData();
  form.append("audio", clip.blob, voiceAudioFileName(clip.mimeType));
  // The context part MUST carry its own application/json content type or
  // Spring rejects the request with 415 (same rule as bulkImageAnalyze).
  form.append(
    "context",
    new Blob([JSON.stringify(context)], { type: "application/json" }),
  );

  let response: Response;
  try {
    response = await postRequest(endpoint, form, true, undefined, { signal });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new VoiceApiError(VOICE_LOCAL_CODES.NETWORK, "Network error", {
      retryable: true,
    });
  }

  return readVoiceResponse(
    response,
    (value): value is VoiceResult =>
      value !== null &&
      typeof value === "object" &&
      "requestId" in value &&
      typeof value.requestId === "string",
  );
}

// ─── Outcome ───────────────────────────────────────────────────────────────

/**
 * Fire-and-forget: records what the user did with a result (metrics and
 * support). Uses keepalive so it survives the popup closing. Never throws.
 */
function reportOutcome(
  requestId: string | null | undefined,
  body: VoiceOutcomeBody,
): void {
  if (!requestId) return;
  const endpoint = ServiceEndpoints.VOICE_OUTCOME.replace(
    "{requestId}",
    encodeURIComponent(requestId),
  );
  try {
    postRequest(endpoint, body, true, undefined, { keepalive: true })
      .then((response) => {
        if (!response.ok)
          voiceLog("outcome.rejected", {
            requestId,
            status: response.status,
            outcome: body.outcome,
          });
      })
      .catch(() =>
        voiceLog("outcome.failed", { requestId, outcome: body.outcome }),
      );
  } catch {
    // never let metrics break the flow
  }
}

export default {
  getCapabilities,
  invalidateCapabilities,
  subscribeCapabilities,
  analyze,
  reportOutcome,
};
